import _ from "underscore";
import DriveView from "../../DriveView";
import { minDate, maxDate } from "../../utils/localizer";
import Sync from "../../sync/sync";
import { getServerDate } from "../../utils/storage/date";
import { extend, htmlUnescape } from "../../utils/vanilla";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { PageLevel } from "../../store/mobile/types";
import Messages from "../../model/Messages";
import Message, {IMessageContent} from "../../model/Message";
import { makeUserCache } from "../../utils/storage/cache";
import { CancelablePromise } from "../../utils/cancelablePromise";
import { getCurrentVehicle } from "../../utils/device";
import { NODEVICEID, NOUSERID } from "../../model/DutyStatusLog";
import { SyncType } from "../../sync/sync.types";
import ButtonsBar from "../framework/buttonsBar";
import { handleApi } from "../../utils/error";
import { IGroup, isSystemMessage, locationLinkClick, messageLinkClick, ViewTypes } from "./helpers";
import pageHTML from "./view.tmpl";
import { MESSAGE_LIMIT } from "../../sync/messages";
import Notifications from "../../utils/notifications";

export default class ViewMessageView extends DriveView<any> {

    protected strings = [
        "Send",
        "Message to {0}"
    ];
    private readonly template = _.template(pageHTML);
    private readonly messages: Messages;
    private readonly sync: Sync;
    private readonly currentVehicle;
    private readonly userCache;
    private readonly users;
    private readonly deviceId: string;
    private readonly currentUser: DeepReadonlyObject<IUser> | null;
    private viewTitle: string;
    private readonly viewType?: ViewTypes;
    private readonly senderId: string;
    private readonly currentUserId: string;
    private buttonsBar?: ButtonsBar;
    private currentMessage: string;

    constructor (options) {
        super(options, {
            title: "Messages",
            activeSection: "messaging",
            activeView: "inbox",
            pageLevel: PageLevel.TWO
        });
        this.app = options.app;
        this.currentUser = this.app.userService.getCurrentUser();
        if (this.currentUser) {
            this.currentUserId = this.currentUser.id;
        }
        this.userCache = makeUserCache(this.app.store);
        this.users = this.userCache.getDict();
        this.messages = new Messages(this.app.store, this.currentUserId);
        this.messages.fetch();
        this.sync = this.app.sync;
        this.currentVehicle = getCurrentVehicle();
        this.viewType = options.state.type;
        if (this.viewType === ViewTypes.Recipient) {
            this.senderId = options.state.id ?? this.currentUserId;
        } else {
            this.deviceId = this.currentVehicle.id;
        }
        this.listenTo(dispatcher, DispatcherEvents.SyncMessages, () => {
            this.currentMessage = ViewMessageView.getNewMessage();
            this.render();
        });
    }

    private static getNewMessage() {
        let contentElement = <HTMLInputElement>document.getElementById("compose__message");
        return contentElement ? contentElement.innerText.trim() : "";
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .cannedResponseOptions": this.cannedResponse,
            "click .locationLink": locationLinkClick,
            "click #send__message": this.normalResponse,
            "click .messageLink": messageLinkClick
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render () {
        let scrollToBottom = () => {
            let scrollDiv = document.querySelector("div#scroll-content");
            if (scrollDiv) {
                let container = document.querySelector("section#content") as HTMLElement;
                if (container) {
                    scrollDiv.scrollTop = container.scrollHeight;
                }
            }
        };

        let renderContent = (msgs: Message[], title: string, type: ViewTypes | undefined) => {
            this.display(this.template({
                messageGroups: this.groupMessagesByDate(msgs),
                viewTitle: title,
                currentUserId: this.currentUserId,
                viewType: type
            })).then(() => {
                if (this.viewType !== ViewTypes.System) {
                    this.createSendButton();
                }
                this.app.setFullWidth(true);
                scrollToBottom();
            })
            .catch(handleApi);
        };

        this.viewTitle = this.getViewTitle(this.viewType);

        let allMessages = this.messages.getMessages({
            sortAscending: true,
            groupRouteMessages: true
        });

        let filteredMsgs = this.filterMessages(allMessages, this.viewType);

        // Mark unread messages as read
        this.markMessagesAsRead(filteredMsgs);

        // Populate RouteLocationUri
        let routeInfoUriPromises: CancelablePromise<void>[] = [];

        let getParentCannedMessage = (parentMsgId: string) => {
            let parentMessage = filteredMsgs.filter(item => item.id === parentMsgId)[0];
            return parentMessage && parentMessage.messageContent?.contentType === "CannedResponse" ? parentMessage : null;
        };

        if (filteredMsgs && filteredMsgs.length > 0 && (this.viewType === ViewTypes.Device || this.viewType === ViewTypes.Recipient)) {
            filteredMsgs.forEach(mes => {
                this.populateMessageLocationUri(mes, filteredMsgs, routeInfoUriPromises);

                let parentMsgSlim = mes.parentMessage;
                let parentCannedMessage = parentMsgSlim?.id && getParentCannedMessage(parentMsgSlim.id);
                if (parentCannedMessage) {
                    parentCannedMessage._cannedReply = mes.getContent() || undefined;
                }
            });
        }

        void Promise.all(routeInfoUriPromises).then(() => {
            if (routeInfoUriPromises.length > 0) {
                renderContent(filteredMsgs, this.viewTitle, this.viewType);
            }
            return this;
        });

        renderContent(filteredMsgs, this.viewTitle, this.viewType);
        return this;
    }

    groupMessagesByDate = (messages: Message[]) => {
        const messageGroups = messages.reduce((groups: IGroup[], message: Message) => {
            const sentDate = message.getSentDate();
            if (sentDate) {
                if (!groups[sentDate]) {
                    groups[sentDate] = [];
                }
                groups[sentDate].push(message);
            }
            return groups;
        }, new Array<IGroup>());

        return Object.keys(messageGroups).map((date) => {
            return { date: date, messages: messageGroups[date] } as IGroup;
        });
    };

    markMessagesAsRead = (messages: Message[]) => {
        let counter = 0;
        messages.forEach(message => {
            let content = message.messageContent;
            let sender = message.user;
            let senderId = sender?.id !== NOUSERID && sender?.id;
            let isDirectionToVehicle = message.isDirectionToVehicle;
            let device = message.device;

            let isAdminSameAsLoggedInUser = senderId === this.currentUser?.id;
            let isIncomingCurrentDeviceMsg = isDirectionToVehicle && device?.id !== NODEVICEID && device?.id === this.deviceId;

            // Mark messages that are sent from admin (different from current user), system messages and device messages where 'admin' and 'driver' are the same as "read"
            if ((senderId !== this.currentUser?.id) || sender === null || (isIncomingCurrentDeviceMsg && isAdminSameAsLoggedInUser) && counter < MESSAGE_LIMIT) {
                if (content?.routeId) {
                    let routeMessages = this.messages.getMessages(undefined).filter(routeMsg => {
                        return routeMsg.messageContent?.routeId && !routeMsg.read && routeMsg.delivered && routeMsg.messageContent.routeId === content?.routeId;
                    });

                    // Only tick the counter once per route message.
                    if (routeMessages.length) {
                        counter++;
                    }

                    routeMessages.forEach(routeMsg => {
                        routeMsg.read = getServerDate().toISOString();
                        routeMsg.markedReadBy = { id: this.currentUserId };
                        void this.sync.messages.setItem(routeMsg, true);
                    });
                } else if (!message.read && message.delivered) {
                    message.read = getServerDate().toISOString();
                    message.markedReadBy = { id: this.currentUserId };
                    void this.sync.messages.setItem(message, true);
                    counter++;
                }
            }
        });
        if (counter) {
            // If we've marked messages as read, update the unread messages notification if it exists (it should).
            let currentMessageNotifications = Notifications.get("unreadMessages", this.currentUserId);
            if (currentMessageNotifications !== null && currentMessageNotifications.count) {
                Notifications.updateUnreadMessagesNotification(currentMessageNotifications.count - counter, this.currentUserId);
            }

            // Sync can happen later
            void this.sync.synchronize(false, null, [SyncType.Messages]);
        }
    };

    filterMessages = (messages: Message[], viewType?: ViewTypes) => {

        let filteredMessages: Message[] = [];
        const isValidDeviceMessage = (msg: Message) => {
            return msg.device?.id !== NODEVICEID && msg.device?.id === this.deviceId;
        };

        if (viewType === ViewTypes.Device) {

            let deviceMessages = messages.filter(msg => {
                let messageContent = msg.messageContent;
                return isValidDeviceMessage(msg) && messageContent && !isSystemMessage(msg);
            });

            deviceMessages.forEach((msg) => {
                this.populateRouteInfo(msg);
                filteredMessages.push(msg);
            });
        } else if (viewType === ViewTypes.Recipient) {

            messages.forEach((item) => {
                let sender = item.user;
                let recipient = item.recipient;
                if (recipient?.id !== NOUSERID && sender?.id !== NOUSERID) {

                    // Get all incoming and outgoing messages between sender and the recipient (i.e current user).
                    if ((sender?.id === this.senderId && recipient?.id === this.currentUserId) || (recipient?.id === this.senderId && sender?.id === this.currentUserId)) {
                        this.populateRouteInfo(item);
                        filteredMessages.push(item);
                    }
                }
            });

            // Show viewTitle as Administrator for message that user sent to themselves
            if (this.users.hasOwnProperty(this.senderId) && this.senderId !== this.currentUserId) {
                this.viewTitle = `${ this.users[this.senderId].firstName } ${ this.users[this.senderId].lastName }`;
            } else {
                this.viewTitle = "Administrator";
            }

        } else {

            let deviceMessages = messages.filter(isValidDeviceMessage);
            deviceMessages.forEach((item) => {
                if (isSystemMessage(item)) {
                    filteredMessages.push(item);
                }
            });
        }
        return filteredMessages;
    };

    getViewTitle = (viewType?: ViewTypes) => {
        let viewTitle;
        switch (viewType) {
            case ViewTypes.Device:
                viewTitle = "Dispatch";
                break;
            case ViewTypes.Recipient:
                viewTitle = "Recipient";
                break;
            case ViewTypes.System:
                viewTitle = "From System";
                break;
            default:
                viewTitle = "Dispatch";
                break;
        }
        return viewTitle;
    };

    populateMessageLocationUri = (mes: Message, filteredMessages: Message[], routeInfoUriPromises: CancelablePromise<void>[]) => {
        let messageLocationUri: string[] = [];
        let content = mes.messageContent;

        let routeId = content?.routeId;

        let setMessageLocationUri = (uri: string, item: Message) => {
            let foundIndex = filteredMessages.indexOf(item);
            if (foundIndex !== -1) {
                messageLocationUri.push(uri);
                item._messageLocationUri = messageLocationUri;
                filteredMessages[foundIndex] = item;
            }
        };

        if (routeId) {
            let stopAddress = new Message();
            let routeInfo = mes._routeInfo;
            routeInfo?.forEach(route => {
                stopAddress.messageContent = route;
                routeInfoUriPromises.push(stopAddress.getAddressUri().then(uri => {
                    setMessageLocationUri(uri, mes);
                }));
            });
        } else {
            if (content?.contentType === "Location" && (content.latitude || content.longitude)) {
                routeInfoUriPromises.push(mes.getAddressUri().then(uri => {
                    setMessageLocationUri(uri, mes);
                }));
            }
        }
    };

    populateRouteInfo = (mes: Message) => {
        if (!this.currentUserId) {
            return;
        }
        let content = mes.messageContent;
        let routeId = content?.routeId;
        if (routeId) {
            let routes = this.app.store.getState().routes[this.currentUserId] || {};
            let routeInfo = this.messages.getRelatedRouteMessages(mes).map(m => m.messageContent) as IMessageContent[];
            let routeName = routes && routes.hasOwnProperty(routeId) ? routes[routeId].name : undefined;
            mes._routeInfo = routeInfo;
            mes._routeName = routeName;
        }
    };

    normalResponse () {
        let content = ViewMessageView.getNewMessage();
        let message: Message;

        if (content.length === 0) {
            return;
        }

        message = new Message({
            id: "",
            activeFrom: minDate.toISOString(),
            activeTo: maxDate.toISOString(),
            sent: getServerDate().toISOString(),
            messageContent: {
                contentType: "Normal",
                message: content
            },
            user: this.currentUserId ? { id: this.currentUserId } : undefined,
            isDirectionToVehicle: false
        });

        if (this.deviceId && this.deviceId !== NODEVICEID) {
            message.device = { id: this.deviceId };
        } else if (this.senderId) {
            message.recipient = { id: this.senderId };
        } else if (!this.deviceId && !this.senderId) {
            message.recipient = { id: this.currentUserId };
        }

        this.buttonsBar && this.buttonsBar.waiting.start();

        this.sync.messages.addItem(message)
            .then(() => {
                this.render();
                this.currentMessage = "";
            })
            .catch(e => {
                this.buttonsBar && this.buttonsBar.waiting.stop();
                handleApi(e);
            });
    }

    toggleButtons(targetId: string, disable: boolean) {
        let cannedResponseElement = targetId && document.getElementById(`ul#${ targetId }`) as HTMLElement;
        if (cannedResponseElement) {
            let cannedReponseOptions = cannedResponseElement.getElementsByTagName("button");
            // Only show the selected canned response option and disable remaining options
            for (let i = 0; i < cannedReponseOptions.length; i++) {
                cannedReponseOptions[i].disabled = disable;
            }
        }
    }

    cannedResponse (e) {
        this.toggleButtons(e.currentTarget.id, true);

        let text = htmlUnescape(e.currentTarget.innerHTML);
        let message = new Message({
            id: "",
            activeFrom: minDate.toISOString(),
            activeTo: maxDate.toISOString(),
            sent: getServerDate().toISOString(),
            messageContent: {
                contentType: "Normal",
                message: text
            },
            isDirectionToVehicle: false,
            user: this.currentUserId ? { id: this.currentUserId } : undefined,
            parentMessage: {
                id: e.currentTarget.parentElement.parentElement.id,
                isDirectionToVehicle: false
            }
        });

        if (this.deviceId) {
            message.device = { id: this.deviceId };
        } else if (this.senderId) {
            message.recipient = { id: this.senderId };
        }

        this.sync.messages.addItem(message)
            .then(() => this.render())
            .catch(error => {
                handleApi(error);
                this.toggleButtons(e.currentTarget.id, false);
            });
    }

    remove() {
        this.buttonsBar && this.buttonsBar.hide();
        delete this.buttonsBar;
        this.app.setFullWidth(false);
        super.remove();
        return this;
    }

    private createSendButton() {
        this.buttonsBar && this.buttonsBar.hide();

        let userAcl: string[] = [];
        if (this.currentUserId) {
            userAcl = extend(true, [], this.app.store.getState().data.acl[this.currentUserId] || []);
        }
        const hasGrpMsgPerm = userAcl.some(e => e.includes("SendGpsTextMessage"));
        this.buttonsBar = new ButtonsBar([{
            title: this.getString("Send"),
            id: "send__message",
            clickHandler: () => this.normalResponse()
        }], {
            isNightMode: this.app.store.getState().data.nightMode,
            textArea: {
                id: "compose__message",
                placeHolderText: this.getString("Message to {0}").replace("{0}", this.viewTitle),
                defaultMessage: this.currentMessage,
                contentEditable: hasGrpMsgPerm
            }
        });
        this.buttonsBar.show();
    }
}